import { Controller } from "@hotwired/stimulus";

export class PurchaseDiscountController extends Controller {
  static targets = [
    // Modal
    'confirmModal',
    // PurchaseTicket
    'purchaseTicketUnitField',
    'numberOfTicket',
    'ticketTermsCheckbox',
    // PurchaseTicketConfirm
    'purchaseTicketUnitValue',
    'confirmNumberOfTicket',
    'confirmPurchaseTicketPrice',
    // Discount inputs
    'pointCheckbox',
    'pointInputWrapper',
    'pointInput',
    'pointError',
    'harvestCheckbox',
    'harvestInputWrapper',
    'harvestInput',
    'harvestError',
    // Summary
    'basePrice',
    'summaryBasePrice',
    'summaryPointDiscount',
    'summaryHarvestDiscount',
    'depositRequired',
    'paymentAmount',
    'paymentInfo',
    'globalError',
    'submitBtn',
    // Notes Accordion
    'notesAccordion',
    'notesToggle',
    'notesContent',
  ]

  static values = {
    price: Number,
    availablePoints: Number,
    availableHarvests: Number,
    projectTitle: String,
  }

  connect() {
    this.availablePoints = this.availablePointsValue;
    this.availableHarvests = this.availableHarvestsValue;
    this.basePrice = 0;

    this.updateByPurchaseTicketUnitField();

    if (this.hasPurchaseTicketUnitFieldTarget) {
      this.purchaseTicketUnitFieldTarget.addEventListener(
        "input", this.updateByPurchaseTicketUnitField.bind(this));
    }
  }

  disconnect() {
    if (this.hasPurchaseTicketUnitFieldTarget) {
      this.purchaseTicketUnitFieldTarget.removeEventListener(
        "input", this.updateByPurchaseTicketUnitField.bind(this));
    }
  }

  // チケット数量変更時の処理
  updateByPurchaseTicketUnitField() {
    const unit = Number(this.purchaseTicketUnitFieldTarget.value);
    if (unit === '') { return }

    this.basePrice = this.getTotalPrice(unit);

    if (this.hasConfirmPurchaseTicketPriceTarget) {
      this.confirmPurchaseTicketPriceTarget.textContent = this.formatCurrency(this.basePrice);
    }
    if (this.hasConfirmNumberOfTicketTarget) {
      this.confirmNumberOfTicketTarget.textContent = unit;
    }
    if (this.hasNumberOfTicketTarget) {
      this.numberOfTicketTarget.textContent = unit;
    }
    if (this.hasPurchaseTicketUnitValueTarget) {
      this.purchaseTicketUnitValueTarget.value = unit;
    }
  }

  // 合計金額を取得
  getTotalPrice(unit) {
    return unit * 10000
  }

  // カンマ付き数値に変換
  formatCurrency(value) {
    return value.toLocaleString('en-US');
  }

  // チケット購入Submit
  onPurchaseSubmit(event) {
    event.preventDefault();
    if (this.purchaseTicketUnitFieldTarget.value === '' || !this.ticketTermsCheckboxTarget.checked) {
      return;
    }

    this.openModal();
  }

  // ポイント使用チェックボックストグル
  togglePointInput() {
    if (this.pointCheckboxTarget.checked) {
      this.pointInputWrapperTarget.style.display = 'block';
      this.pointInputTarget.focus();
    } else {
      this.pointInputWrapperTarget.style.display = 'none';
      this.pointInputTarget.value = '';
      this.calculateTotal();
    }
  }

  // 収穫金使用チェックボックストグル
  toggleHarvestInput() {
    if (this.harvestCheckboxTarget.checked) {
      this.harvestInputWrapperTarget.style.display = 'block';
      this.harvestInputTarget.focus();
    } else {
      this.harvestInputWrapperTarget.style.display = 'none';
      this.harvestInputTarget.value = '';
      this.calculateTotal();
    }
  }

  // ポイント残額使用
  useRemainingPoints() {
    const harvestAmount = Number(this.harvestInputTarget.value) || 0;
    const remaining = Math.max(this.basePrice - harvestAmount, 0);
    const maxAmount = Math.min(this.availablePoints, remaining);
    this.pointInputTarget.value = maxAmount;
    this.calculateTotal();
  }

  // 収穫金残額使用
  useRemainingHarvests() {
    const pointAmount = Number(this.pointInputTarget.value) || 0;
    const remaining = Math.max(this.basePrice - pointAmount, 0);
    const maxAmount = Math.min(this.availableHarvests, remaining);
    this.harvestInputTarget.value = maxAmount;
    this.calculateTotal();
  }

  // ポイントリセット
  resetPoints() {
    this.pointInputTarget.value = '';
    this.calculateTotal();
  }

  // 収穫金リセット
  resetHarvests() {
    this.harvestInputTarget.value = '';
    this.calculateTotal();
  }

  // リアルタイム計算
  calculateTotal() {
    this.clearErrors();

    const pointAmount = Number(this.pointInputTarget.value) || 0;
    const harvestAmount = Number(this.harvestInputTarget.value) || 0;

    // バリデーション
    let hasError = false;

    if (pointAmount > this.availablePoints) {
      this.showError(this.pointErrorTarget, `保有残高（${this.formatCurrency(this.availablePoints)}円）を超えています`);
      hasError = true;
    }

    if (harvestAmount > this.availableHarvests) {
      this.showError(this.harvestErrorTarget, `保有残高（${this.formatCurrency(this.availableHarvests)}円）を超えています`);
      hasError = true;
    }

    const totalDiscount = pointAmount + harvestAmount;
    if (totalDiscount > this.basePrice) {
      this.showError(this.globalErrorTarget, '使用額合計が購入金額を超えています');
      hasError = true;
    }

    // サマリー更新
    const depositRequired = Math.max(this.basePrice - totalDiscount, 0);

    this.summaryBasePriceTarget.textContent = `${this.formatCurrency(this.basePrice)}円`;
    this.summaryPointDiscountTarget.textContent = `- ${this.formatCurrency(pointAmount)}円`;
    this.summaryHarvestDiscountTarget.textContent = `- ${this.formatCurrency(harvestAmount)}円`;
    this.depositRequiredTarget.textContent = `${this.formatCurrency(depositRequired)}円`;

    // 振込が必要な場合のみpayment-infoとアコーディオンを表示
    if (depositRequired > 0) {
      this.paymentAmountTarget.textContent = `${this.formatCurrency(depositRequired)}円`;
      this.paymentInfoTarget.style.display = 'flex';
      this.notesAccordionTarget.style.display = 'block';
    } else {
      this.paymentInfoTarget.style.display = 'none';
      this.notesAccordionTarget.style.display = 'none';
    }

    // 送信ボタン制御
    this.submitBtnTarget.disabled = hasError;
  }

  // エラー表示
  showError(target, message) {
    target.textContent = message;
    target.style.display = 'block';
  }

  // エラークリア
  clearErrors() {
    if (this.hasPointErrorTarget) {
      this.pointErrorTarget.textContent = '';
      this.pointErrorTarget.style.display = 'none';
    }
    if (this.hasHarvestErrorTarget) {
      this.harvestErrorTarget.textContent = '';
      this.harvestErrorTarget.style.display = 'none';
    }
    if (this.hasGlobalErrorTarget) {
      this.globalErrorTarget.textContent = '';
      this.globalErrorTarget.style.display = 'none';
    }
  }

  // モック用の購入確定処理
  onMockConfirmSubmit(event) {
    event.preventDefault();

    const unit = this.purchaseTicketUnitFieldTarget.value;
    const pointAmount = Number(this.pointInputTarget.value) || 0;
    const harvestAmount = Number(this.harvestInputTarget.value) || 0;
    const depositRequired = Math.max(this.basePrice - pointAmount - harvestAmount, 0);

    // 購入データをURLパラメータとして購入完了画面に遷移
    const params = new URLSearchParams({
      project_title: this.projectTitleValue || '【モック】テストプロジェクト',
      unit: unit,
      base_price: this.basePrice,
      point_discount: pointAmount,
      harvest_discount: harvestAmount,
      deposit_required: depositRequired
    });

    window.location.href = `/purchase_discount_mocks/complete?${params.toString()}`;
  }

  // 閉じるボタンClick
  onCloseButton(event) {
    event.preventDefault();
    this.closeModal(this.confirmModalTarget);
  }

  // モーダルを開く
  openModal() {
    // モーダルを開く際に基本価格を初期化
    this.basePrice = this.getTotalPrice(this.purchaseTicketUnitFieldTarget.value);

    if (this.hasBasePriceTarget) {
      this.basePriceTarget.textContent = `${this.formatCurrency(this.basePrice)}円`;
    }

    // 初期計算
    this.calculateTotal();

    // モーダルを表示
    this.displayTarget(this.confirmModalTarget);
  }

  // 指定されたモーダルを閉じる
  closeModal(target) {
    target.style.display = 'none';
    target.style.opacity = 0;

    // モーダルを閉じる際にリセット
    if (this.hasPointCheckboxTarget) {
      this.pointCheckboxTarget.checked = false;
      this.pointInputWrapperTarget.style.display = 'none';
      this.pointInputTarget.value = '';
    }
    if (this.hasHarvestCheckboxTarget) {
      this.harvestCheckboxTarget.checked = false;
      this.harvestInputWrapperTarget.style.display = 'none';
      this.harvestInputTarget.value = '';
    }
  }

  // 指定された要素を表示
  displayTarget(target) {
    target.style.display = 'block';
    target.style.opacity = 1;
  }

  // 注意事項アコーディオンのトグル
  toggleNotes() {
    const content = this.notesContentTarget;
    const icon = this.notesToggleTarget.querySelector('.purchase-discount-mock__notes-toggle-icon');

    if (content.style.display === 'none') {
      content.style.display = 'block';
      icon.textContent = '▲';
    } else {
      content.style.display = 'none';
      icon.textContent = '▼';
    }
  }
}
